import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSiteState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbItem,
    Breadcrumbs,
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    HeaderBarSkeleton,
    LoadingPanel,
    Page,
    PageHeader,
    PageMain,
    toast,
} from '@gitbook/spine-react';
import { HeadTitle } from '@gitbook/util-react-head';
import { assertNever } from '@gitbook/util-ts';

import {
    APIErrorBoundary,
    SiteCustomizationScreen,
    SitePreviewScreen,
    SiteVisitorExpressionContextProvider,
} from '../../Components';
import {
    NavigationRedirect,
    NavigationScreenWithOrganization,
    useSetAnalyticsScreen,
} from '../../navigation';
import {
    apiQuery,
    checkIsSiteStructureEmpty,
    getSitePermissions,
    SiteCapabilityProvider,
    SiteContextProvider,
    useMarkContentAsRecentlyVisited,
} from '../../state';
import { IntegrationsModal } from '../Integrations';
import { SiteInsights } from './components/Insights';
import { SiteUpgradeModal } from './components/Plan/SiteUpgradeModal';
import { UpgradeStatusModal } from './components/Plan/UpgradeStatusModal';
import { SiteEmptyState } from './components/SiteEmptyState';
import { SiteGetStartedTaskId, useSiteGetStartedIgnoredItemIds } from './components/SiteGetStarted';
import { SiteHeader } from './components/SiteHeader';
import { SiteLoadingProvider, useSiteScreenLoading } from './components/SiteLoadingContext';
import { SiteOverview } from './components/SiteOverview';
import { SiteSettings } from './components/SiteSettings';

/**
 * Screen for a site.
 */
export function SiteScreen(props: { screen: NavigationScreenSiteState }) {
    return (
        <NavigationScreenWithOrganization screen={props.screen}>
            {({ screen }) => (
                <React.Suspense fallback={<SiteScreenSkeleton />}>
                    <APIErrorBoundary
                        fallback={{
                            404: <SiteNotFound organizationId={screen.organization} />,
                            403: <SiteNoPermission organizationId={screen.organization} />,
                        }}
                    >
                        <SiteLoadingProvider>
                            <SiteRootLoader screen={screen} />
                        </SiteLoadingProvider>
                    </APIErrorBoundary>
                </React.Suspense>
            )}
        </NavigationScreenWithOrganization>
    );
}

function SiteScreenSkeleton() {
    return (
        <div className="flex flex-1 flex-col">
            <HeaderBarSkeleton />
            <LoadingPanel size="large" className="flex-1" />
        </div>
    );
}

function SiteRootLoader(props: {
    screen: NavigationScreenSiteState & {
        organization: string;
    };
}) {
    const { screen } = props;
    const { organization: organizationId, site: siteId } = screen;

    const ignoredGetStartedItemIds = useSiteGetStartedIgnoredItemIds(siteId);
    const { isLoading } = useSiteScreenLoading();

    const [
        { data: site },
        { data: organization },
        { data: siteStructure },
        { data: siteCustomization },
        { data: orgUsageMembers },
    ] = useSuspenseQueries({
        queries: [
            apiQuery('getSiteById', { organizationId, siteId }),
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getSiteStructure', { organizationId, siteId }),
            apiQuery('getSiteRawCustomizationById', { organizationId, siteId }),
            apiQuery(
                'getMembersUsageForOrganizationById',
                // We skip the query if the user has ignored the invite teammates task.
                !ignoredGetStartedItemIds.has(SiteGetStartedTaskId.InviteTeammates)
                    ? { organizationId }
                    : skipToken
            ),
        ],
    });

    const sitePermissions = React.useMemo(
        () => getSitePermissions({ siteStructure, organization }),
        [siteStructure, organization]
    );

    // Save site ID in local storage as recently visited
    useMarkContentAsRecentlyVisited({ contentId: siteId, organizationId });

    const isEmpty = checkIsSiteStructureEmpty(siteStructure);

    return (
        <SiteContextProvider site={site} organizationId={organizationId}>
            <SiteVisitorExpressionContextProvider organizationId={organizationId} site={site}>
                <SiteCapabilityProvider permissions={sitePermissions}>
                    <HeadTitle title={`${site.title} - Docs sites`} />
                    <SiteScreenTracking screen={screen} isEmpty={isEmpty} />
                    <React.Suspense fallback={<HeaderBarSkeleton />}>
                        <SiteHeader
                            screen={screen}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            siteStructure={siteStructure}
                        />
                    </React.Suspense>
                    <React.Suspense fallback={<LoadingPanel size="large" className="flex-1" />}>
                        {(() => {
                            if (isEmpty || isLoading) {
                                return (
                                    <SiteEmptyState
                                        site={site}
                                        organization={organization}
                                        siteCustomization={siteCustomization}
                                    />
                                );
                            }
                            switch (screen.tab?.type) {
                                case 'settings':
                                    return <SiteSettings section={screen.tab.section} />;
                                case 'insights':
                                    return <SiteInsights tab={screen.tab} />;
                                case 'customization':
                                    return <SiteCustomizationScreen screenTab={screen.tab} />;
                                case 'preview':
                                    return (
                                        <SitePreviewScreen
                                            organization={organization}
                                            site={site}
                                        />
                                    );
                                case undefined:
                                    return (
                                        <SiteOverview
                                            site={site}
                                            siteStructure={siteStructure}
                                            sitePermissions={sitePermissions}
                                            siteCustomization={siteCustomization}
                                            organization={organization}
                                            orgUsageMembers={orgUsageMembers?.members ?? null}
                                        />
                                    );
                                default:
                                    assertNever(screen.tab);
                            }
                        })()}
                    </React.Suspense>

                    {/* Prevent any modal to delay the loading of the page */}
                    <React.Suspense fallback={null}>
                        <SiteUpgradeModal />
                        <UpgradeStatusModal screen={screen} />
                        <IntegrationsModal screen={screen} />
                    </React.Suspense>
                </SiteCapabilityProvider>
            </SiteVisitorExpressionContextProvider>
        </SiteContextProvider>
    );
}

function SiteScreenTracking(props: { screen: NavigationScreenSiteState; isEmpty: boolean }) {
    const { screen, isEmpty } = props;
    const screenTab = screen.tab?.type;
    const screenSection = screen.tab?.type === 'settings' ? screen.tab.section : undefined;
    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: screen.site, organization: screen.organization },
            {
                screen: 'site',
                screenTab,
                screenSection,
                isEmpty,
            }
        ),
        [screen.site, screen.organization, screenTab, screenSection, isEmpty]
    );

    return null;
}

function SiteNotFound(props: { organizationId: string }) {
    const { organizationId } = props;
    return (
        <Page>
            <HeadTitle title="Site not found" />
            <PageHeader>
                <Breadcrumbs>
                    <BreadcrumbItem
                        icon={Icon.DocsSite}
                        label="Docs sites"
                        href={{
                            screen: {
                                type: 'sites',
                                organization: organizationId,
                            },
                        }}
                    />
                    <BreadcrumbItem label="Site not found" />
                </Breadcrumbs>
            </PageHeader>
            <PageMain>
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.DocsSite />
                    </EmptyStateIcon>
                    <EmptyStateNotFoundHeading />
                    <Description>
                        The docs site you are trying to access doesn't exist or may have been
                        deleted.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            kind="secondary"
                            size="medium"
                            href={{
                                screen: { type: 'sites', organization: organizationId },
                            }}
                            label="Browse sites"
                        />
                    </EmptyStateActions>
                </EmptyState>
            </PageMain>
        </Page>
    );
}

function SiteNoPermission(props: { organizationId: string }) {
    React.useEffect(() => {
        toast.error('Error while accessing site', null, {
            description: 'You do not have access to this site.',
        });
    }, []);

    return (
        <NavigationRedirect
            to={{ screen: { type: 'home', organization: props.organizationId } }}
            replace
        />
    );
}
